import { z } from "zod";
import { browserEnvSchema, Environment } from "./browser-env";

/**
 * Shared cross-service ("common") env — the strict canonical schema for the product-v2
 * apps. Every key is required with no fallback; `.env`/`.env.local` are the sole source,
 * no develop/local defaults. Exception: the Axiom trio, inherited via `safeExtend` from
 * `browserEnvSchema`, stays optional under `develop` and required otherwise. The v1 apps keep their lenient posture via `legacyEnvSchema` below, unaffected by this tightening.
 */
export const envSchema = browserEnvSchema.safeExtend({
	/**
	 * Server-/v1-only common key (not in `browserEnvDefinitions`, never projected into a v2
	 * browser bundle). v2 realtime authenticates the browser with short-TTL, server-minted
	 * Ably tokens, so v2 apps neither set nor read this; kept (optional, default "") for
	 * v1 apps holding a browser Ably key and the api's boot-time app-mismatch safeguard — unlike the ingest-only Axiom trio, this is a reusable credential that stays out of browser bundles.
	 */
	COMMON_ENV_ABLY_API_KEY: z.string().default(""),
});

export type EnvConfig = z.infer<typeof envSchema>;
export type EnvKey = keyof EnvConfig;
export const envKeys = Object.keys(envSchema.shape) as EnvKey[];

/**
 * Frozen lenient legacy schema — the pre-strict posture (every key carries a
 * develop/local default) preserved verbatim for the v1 apps (webapp / retail-app
 * / pricing / vision-ai / cms-wrapper) so they keep booting unchanged while v2 moves
 * to the strict `envSchema`. Do not extend this; v1 migrates to the strict schema later.
 */
export const legacyEnvSchema = z.object({
	COMMON_ENV_PLATFORM_URL: z.url().default("http://localhost:4000"),
	COMMON_ENV_WEBAPP_URL: z.url().default("http://localhost:3000"),
	COMMON_ENV_CMS_URL: z.url().or(z.literal("")).default(""),
	COMMON_ENV_PRICING_URL: z.url().or(z.literal("")).default(""),
	COMMON_ENV_GRADER_URL: z.url().or(z.literal("")).default(""),
	COMMON_ENV_COURIER_URL: z.url().or(z.literal("")).default(""),
	COMMON_ENV_VISION_AI_URL: z.string().default(""),
	COMMON_ENV_CDN_URL: z.url().default("http://localhost:4000"),
	COMMON_ENV_URL_SHORTENER: z.url().or(z.literal("")).default(""),
	COMMON_ENV_ENVIRONMENT: z.enum(Environment).default("develop"),
	COMMON_ENV_ABLY_API_KEY: z.string().default(""),
	COMMON_ENV_AXIOM_DATASET: z.string().default(""),
	COMMON_ENV_AXIOM_ORG_ID: z.string().default(""),
	COMMON_ENV_AXIOM_TOKEN: z.string().default(""),
	COMMON_ENV_POSTHOG_KEY: z.string().default(""),
	COMMON_ENV_POSTHOG_HOST: z.url().or(z.literal("")).default(""),
	COMMON_ENV_GOOGLE_API_KEY: z.string().default(""),
	COMMON_ENV_MAPBOX_TOKEN: z.string().default(""),
});

function pickCommonKeys(envSource: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> {
	const raw: Record<string, unknown> = {};
	for (const key of keys) {
		raw[key] = envSource[key];
	}
	return raw;
}

/**
 * STRICT common-env resolution for the product-v2 apps. Each app calls this with
 * its own environment source (`import.meta.env` or `process.env`). A missing or
 * empty `COMMON_ENV_*` value throws — there is no fallback.
 */
export function createEnvConfig(envSource: Record<string, unknown>): EnvConfig {
	const result = envSchema.safeParse(pickCommonKeys(envSource, envKeys));
	if (!result.success) {
		console.error("❌ Invalid environment variables:", z.treeifyError(result.error));
		throw new Error("Invalid environment variables");
	}
	return result.data;
}

/**
 * LENIENT common-env resolution for the v1 apps — every key defaults to its
 * develop/local value, so a bare boot never throws (the pre-strict behavior).
 */
export function createLegacyEnvConfig(envSource: Record<string, unknown>): EnvConfig {
	const result = legacyEnvSchema.safeParse(pickCommonKeys(envSource, envKeys));
	if (!result.success) {
		console.error("❌ Invalid environment variables:", z.treeifyError(result.error));
		throw new Error("Invalid environment variables");
	}
	return result.data;
}
